import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { commesseAPI, clientiAPI, risorseAPI } from '../lib/api';
import { formatCurrency, formatPercent, formatDate, getStatusLabel } from '../lib/utils';
import { StatusBadge } from '../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Pencil, 
  Trash2, 
  Briefcase,
  Loader2,
  ArrowUpDown,
  Filter
} from 'lucide-react';

const tipoCommessaOptions = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'interna_rd', label: 'R&D' },
  { value: 'interna_presales', label: 'Presales' },
  { value: 'interna_formazione', label: 'Formazione' },
  { value: 'interna_admin', label: 'Admin' },
];

const statoOptions = [
  { value: 'pianificazione', label: 'Pianificazione' },
  { value: 'attiva', label: 'Attiva' },
  { value: 'sospesa', label: 'Sospesa' },
  { value: 'chiusa', label: 'Chiusa' },
];

const initialFormData = {
  codice_commessa: '',
  nome_progetto: '',
  cliente_id: '',
  tipo_commessa: 'cliente',
  project_manager_id: '',
  data_inizio: '',
  data_fine_prevista: '',
  stato: 'pianificazione',
  budget_contratto: 0,
  margine_target: 0,
  descrizione: ''
};

export const CommessePage = () => {
  const navigate = useNavigate();
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [risorse, setRisorse] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommessa, setEditingCommessa] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStato, setFilterStato] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [commesseRes, clientiRes, risorseRes] = await Promise.all([
        commesseAPI.getAll(),
        clientiAPI.getAll(),
        risorseAPI.getAll()
      ]);
      setCommesse(commesseRes.data);
      setClienti(clientiRes.data);
      setRisorse(risorseRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const generateCodiceCommessa = () => {
    const year = new Date().getFullYear();
    const count = commesse.length + 1;
    return `PRJ-${year}-${String(count).padStart(3, '0')}`;
  };

  const handleOpenDialog = (commessa = null) => {
    if (commessa) {
      setEditingCommessa(commessa);
      setFormData({
        codice_commessa: commessa.codice_commessa || '',
        nome_progetto: commessa.nome_progetto || '',
        cliente_id: commessa.cliente_id || '',
        tipo_commessa: commessa.tipo_commessa || 'cliente',
        project_manager_id: commessa.project_manager_id || '',
        data_inizio: commessa.data_inizio || '',
        data_fine_prevista: commessa.data_fine_prevista || '',
        stato: commessa.stato || 'pianificazione',
        budget_contratto: commessa.budget_contratto || 0,
        margine_target: commessa.margine_target || 0,
        descrizione: commessa.descrizione || ''
      });
    } else {
      setEditingCommessa(null);
      setFormData({
        ...initialFormData,
        codice_commessa: generateCodiceCommessa()
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCommessa(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = {
      ...formData,
      budget_contratto: parseFloat(formData.budget_contratto) || 0,
      margine_target: parseFloat(formData.margine_target) || 0,
      cliente_id: formData.cliente_id || null,
      project_manager_id: formData.project_manager_id || null
    };

    try {
      if (editingCommessa) {
        await commesseAPI.update(editingCommessa.id, submitData);
        toast.success('Commessa aggiornata con successo');
      } else {
        await commesseAPI.create(submitData);
        toast.success('Commessa creata con successo');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving commessa:', error);
      toast.error('Errore nel salvataggio della commessa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commessa) => {
    if (window.confirm(`Sei sicuro di voler eliminare "${commessa.nome_progetto}"?`)) {
      try {
        await commesseAPI.delete(commessa.id);
        toast.success('Commessa eliminata con successo');
        loadData();
      } catch (error) {
        console.error('Error deleting commessa:', error);
        toast.error('Errore nell\'eliminazione della commessa');
      }
    }
  };

  const filteredCommesse = commesse.filter(commessa => {
    const matchesSearch = 
      commessa.nome_progetto.toLowerCase().includes(searchQuery.toLowerCase()) ||
      commessa.codice_commessa.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (commessa.cliente_nome && commessa.cliente_nome.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStato = filterStato === 'all' || commessa.stato === filterStato;
    const matchesTipo = filterTipo === 'all' || commessa.tipo_commessa === filterTipo;
    
    return matchesSearch && matchesStato && matchesTipo;
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="commesse-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commesse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Commesse</h1>
          <p className="text-muted-foreground">Gestisci progetti e commesse</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-commessa-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Commessa
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca commesse..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-commesse-input"
          />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-40" data-testid="filter-stato-select">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {statoOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-40" data-testid="filter-tipo-select">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {tipoCommessaOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codice</TableHead>
                <TableHead>Progetto</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Margine</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCommesse.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nessuna commessa trovata</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCommesse.map((commessa) => (
                  <TableRow key={commessa.id} data-testid={`commessa-row-${commessa.id}`}>
                    <TableCell className="font-mono text-sm">{commessa.codice_commessa}</TableCell>
                    <TableCell className="font-medium">{commessa.nome_progetto}</TableCell>
                    <TableCell>{commessa.cliente_nome || '-'}</TableCell>
                    <TableCell>
                      <StatusBadge status={commessa.tipo_commessa} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={commessa.stato} />
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(commessa.budget_contratto)}
                    </TableCell>
                    <TableCell 
                      className="text-right font-mono"
                      style={{ color: (commessa.kpi?.marginalita_prevista || 0) >= 55 ? '#059669' : '#dc2626' }}
                    >
                      {formatPercent(commessa.kpi?.marginalita_prevista || 0)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`commessa-actions-${commessa.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/commesse/${commessa.id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizza
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenDialog(commessa)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(commessa)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="commessa-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingCommessa ? 'Modifica Commessa' : 'Nuova Commessa'}
            </DialogTitle>
            <DialogDescription>
              {editingCommessa ? 'Modifica i dati della commessa' : 'Inserisci i dati della nuova commessa'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codice_commessa">Codice Commessa *</Label>
                <Input
                  id="codice_commessa"
                  value={formData.codice_commessa}
                  onChange={(e) => setFormData({...formData, codice_commessa: e.target.value})}
                  required
                  data-testid="commessa-codice-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo_commessa">Tipo Commessa *</Label>
                <Select 
                  value={formData.tipo_commessa} 
                  onValueChange={(value) => setFormData({...formData, tipo_commessa: value})}
                >
                  <SelectTrigger data-testid="commessa-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoCommessaOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome_progetto">Nome Progetto *</Label>
              <Input
                id="nome_progetto"
                value={formData.nome_progetto}
                onChange={(e) => setFormData({...formData, nome_progetto: e.target.value})}
                required
                data-testid="commessa-nome-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cliente_id">Cliente</Label>
                <Select 
                  value={formData.cliente_id || "none"} 
                  onValueChange={(value) => setFormData({...formData, cliente_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="commessa-cliente-select">
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun cliente</SelectItem>
                    {clienti.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_manager_id">Project Manager</Label>
                <Select 
                  value={formData.project_manager_id || "none"} 
                  onValueChange={(value) => setFormData({...formData, project_manager_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="commessa-pm-select">
                    <SelectValue placeholder="Seleziona PM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun PM</SelectItem>
                    {risorse.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nome} {r.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stato">Stato</Label>
                <Select 
                  value={formData.stato} 
                  onValueChange={(value) => setFormData({...formData, stato: value})}
                >
                  <SelectTrigger data-testid="commessa-stato-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statoOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_inizio">Data Inizio</Label>
                <Input
                  id="data_inizio"
                  type="date"
                  value={formData.data_inizio}
                  onChange={(e) => setFormData({...formData, data_inizio: e.target.value})}
                  data-testid="commessa-data-inizio-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_fine_prevista">Data Fine Prevista</Label>
                <Input
                  id="data_fine_prevista"
                  type="date"
                  value={formData.data_fine_prevista}
                  onChange={(e) => setFormData({...formData, data_fine_prevista: e.target.value})}
                  data-testid="commessa-data-fine-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budget_contratto">Budget Contratto (€)</Label>
                <Input
                  id="budget_contratto"
                  type="number"
                  min="0"
                  step="100"
                  value={formData.budget_contratto}
                  onChange={(e) => setFormData({...formData, budget_contratto: e.target.value})}
                  data-testid="commessa-budget-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="margine_target">Margine Target (%)</Label>
                <Input
                  id="margine_target"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.margine_target}
                  onChange={(e) => setFormData({...formData, margine_target: e.target.value})}
                  data-testid="commessa-margine-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descrizione">Descrizione</Label>
              <Textarea
                id="descrizione"
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                rows={3}
                data-testid="commessa-descrizione-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="commessa-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingCommessa ? 'Salva Modifiche' : 'Crea Commessa'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommessePage;
